import i18n from "@/i18n";
import { error, warning } from "@/constants/themes-colors/CustomColors";
import SignalStatusEnum from "@/constants/enums/SignalStatusEnum";
/**
 * Cria um novo objeto de estado vazio com propriedades padrão.
 *
 * @returns {Object} Um objeto de estado vazio com valores padrão.
 * - `logs` {Array}: Uma lista de logs.
 * - `showDialog` {boolean}: Indica se um diálogo deve ser exibido.
 * - `showToast` {boolean}: Indica se um toast/notificação deve ser exibido.
 * - `isLoadingTableData` {boolean}: Indica se os dados da tabela estão sendo carregados.
 * - `maxVisibleTurbineChips` {number}: Número máximo de chips de turbinas visíveis.
 * - `maxVisibleManagementChips` {number}: Número máximo de chips de tratativa visíveis.
 */

const EmptyState = function()
{
	return {
		logs:                      [],
		availableAssets:           [],
		availableStatus:           [],
		showDialog:                false,
		showToast:                 false,
		isLoadingTableData:        false,
		search:                    "",
		selectedAssets:            [],
		selectedStatus:            [],
		selectedTableRows:         [],
		selectedManagements:       [],
		previousSelectedAssets:    {},
		allAssetsSelected:         false,
		searchQuery:               "",
		maxVisibleTurbineChips:    5,
		maxVisibleManagementChips: 3,
		dateTimeFilter:            7
	};
};

/**
 * Cria um novo objeto de estado baseado em um estado existente ou em um estado vazio padrão.
 *
 * Caso nenhum estado seja passado, será utilizado um objeto padrão vazio com valores iniciais.
 * @param {Object} [newState=EmptyState()] - Estado base a partir do qual o novo estado será construído.
 * @returns {Object} Um objeto de estado formatado para uso na store.
 */

const State = function(newState = EmptyState())
{
	return {
		availableAssets:           newState.availableAssets,
		availableStatus:           newState.availableStatus,
		selectedAssets:            newState.selectedAssets,
		previousSelectedAssets:    newState.previousSelectedAssets,
		allAssetsSelected:         newState.allAssetsSelected,
		searchQuery:               newState.searchQuery,
		maxVisibleTurbineChips:    newState.maxVisibleTurbineChips,
		maxVisibleManagementChips: newState.maxVisibleManagementChips,
		dateTimeFilter:            newState.dateTimeFilter,
		toast:                     {
			show:    newState.toast.show,
			message: newState.toast.message,
			type:    newState.toast.type
		}
	};
};

/**
 * Cria um novo objeto de visualização de log vazio com propriedades padrão.
 *
 * @returns {Object} Novo log com as propriedades atualizadas.
 * - `monitoringElementId` {number|null}: ID do elemento de monitoramento.
 * - `parentMonitoringElementId` {number|null}: ID do elemento de monitoramento pai.
 * - `windTurbineName` {string}: Nome da turbina eólica.
 * - `trendSignalSettingId` {number|null}: ID da configuração do sinal de tendência.
 * - `trendSignalSettingname` {string}: Nome da configuração do sinal de tendência.
 * - `formattedDateTime` {string}: Data e hora formatadas.
 * - `Value` {string}: Valor associado ao log.
 * - `unit` {string}: Unidade do valor.
 * - `status` {number|null}: Status do valor.
 * - `limit` {number|null}: Limite associado ao valor.
 */
const LogViewEmpty = function()
{
	return {
		notificationLoggingId:     null,
		monitoringElementId:       null,
		parentMonitoringElementId: null,
		windTurbineName:           "",
		trendSignalSettingId:      null,
		formattedDateTime:         "",
		value:                     0,
		unit:                      "",
		status:                    null,
		currentLimit:              null,
		limitAtTime:               null,
		trendFrequencyId:          null,
		signalName:                "",
		lastManagement:            {},
		reports:                   []
	};
};

/**
 * Cria um novo objeto de visualização de log com propriedades padrão.
 *
 * Esta função gera um objeto que representa uma entrada de log com valores predefinidos.
 * @param view - Um objeto opcional para inicializar a visualização do log.
 *
 * @returns Um objeto de visualização de log com propriedades padrão.
 */

const LogView = function(view = LogViewEmpty())
{
	return {
		notificationLoggingId:     view.notificationLoggingId,
		monitoringElementId:       view.monitoringElementId,
		parentMonitoringElementId: view.parentMonitoringElementId,
		windTurbineName:           view.windTurbineName,
		trendSignalSettingId:      view.trendSignalSettingId,
		formattedDateTime:         view.formattedDateTime,
		value:                     view.value,
		unit:                      view.unit,
		status:                    view.status,
		currentLimit:              view.currentLimit,
		limitAtTime:               view.limitAtTime,
		trendFrequencyId:          view.trendFrequencyId,
		signalName:                view.signalName,
		lastManagement:            view.lastManagement,
		reports:                   view.reports,
	};
};

/**
 * Retorna um objeto contendo o texto correspondente ao status e uma classe CSS
 * para estilização visual com base no valor do status informado.
 *
 * @param {number} status - Código numérico do status.
 *        1 - Alerta (vermelho)
 *        2 - Atenção (amarelo)
 *        Qualquer outro valor - exibido como texto padrão sem classe de estilo.
 *
 * @returns {{ text: string, class: string }} Objeto com:
 *          - text: descrição legível do status.
 *          - class: classe CSS para estilização do texto.
 */
function GetStatusDisplay(status)
{
	switch (status)
	{
	case SignalStatusEnum.ALARM:
		return {
			text:  i18n.t("Labels.Alarm"),
			class: "status-alarm",
			color: warning,
		};
	case SignalStatusEnum.TRIP:
		return {
			text:  i18n.t("Labels.Alert"),
			class: "status-alert",
			color: error,
		};
	default:
		return {
			text:  i18n.t("Labels.UnknownStatus"),
			class: "status-unknown",
			color: "grey",
		};
	}
}

/**
 * Cria as configurações (array de objetos) das colunas da tabela.
 * Onde cada objeto representa uma coluna da tabela.
 *
 * @returns Um array de objetos que configuram as colunas da tabela.
 **/
const HeaderTableView = function()
{
	return [
		{
			text:  i18n.t("TableHeaders.Date"), value: "formattedDateTime", align: "start"
		},
		{
			text:  i18n.t("TableHeaders.Turbine"), value: "windTurbineName", align: "center"
		},
		{
			text:  i18n.t("TableHeaders.Signal"), value: "signalName", align: "center"
		},
		{
			text:  i18n.t("TableHeaders.Severity"), value: "status", align: "center"
		},
		{
			text:  i18n.t("TableHeaders.Value"), value: "value", align: "center"
		},
		{
			text:  i18n.t("TableHeaders.CrossedLimit"), value: "limitAtTime", align: "center"
		},
		{
			text:  i18n.t("TableHeaders.CurrentLimit"), value: "currentLimit", align: "center"
		},
		{
			text:  i18n.t("TableHeaders.Action"), value: "action", align: "center"
		},
		{
			text:  i18n.t("Buttons.Reports"), value: "reports", align: "center"
		},
		{
			text:  i18n.t("Labels.Tratative"), value: "lastManagement.managementType", align: "center"
		},
	];
};
export default {
	HeaderTableView, LogView, LogViewEmpty, EmptyState, State, GetStatusDisplay
};